import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { CreateUserPayload, UpdateUserPayload, UserProfile, UserRole } from '../../core/models/user.models';
import { AdminUsersService } from '../../core/services/admin-users.service';
import { AuthService } from '../../core/services/auth.service';

@Component({
  selector: 'app-users-page',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink],
  templateUrl: './users-page.component.html',
  styleUrl: './users-page.component.scss',
})
export class UsersPageComponent implements OnInit {
  private readonly usersService = inject(AdminUsersService);
  private readonly auth = inject(AuthService);
  private readonly fb = inject(FormBuilder);

  readonly roles: UserRole[] = ['USER', 'MANAGER', 'ADMIN', 'ADMIN_READ_ONLY'];

  users: UserProfile[] = [];
  loading = false;
  saving = false;
  deletingUserId: number | null = null;
  editingUserId: number | null = null;

  searchQuery = '';
  currentPage = 0;
  pageSize = 12;
  totalPages = 1;
  totalItems = 0;

  errorMessage = '';
  successMessage = '';

  readonly form = this.fb.group({
    firstname: ['', [Validators.required, Validators.minLength(2)]],
    lastname: ['', [Validators.required, Validators.minLength(2)]],
    email: ['', [Validators.required, Validators.email]],
    password: [''],
    role: ['USER' as UserRole, Validators.required],
    enabled: [true],
    displayName: [''],
    biography: [''],
    avatarUrl: [''],
  });

  ngOnInit(): void {
    this.loadUsers();
  }

  loadUsers(page = this.currentPage): void {
    this.loading = true;
    this.errorMessage = '';

    this.usersService.list(page, this.pageSize, this.searchQuery).subscribe({
      next: (response) => {
        const fetchedUsers = response.items ?? [];
        this.users = fetchedUsers;
        this.currentPage = response.currentPage ?? page;
        this.totalItems = response.total ?? fetchedUsers.length;
        const computedTotalPages = Math.ceil((response.total ?? fetchedUsers.length) / this.pageSize) || 1;
        this.totalPages = Math.max(1, response.totalPages ?? computedTotalPages);
      },
      error: (error) => {
        this.errorMessage = error?.error?.message ?? 'Impossible de charger les utilisateurs.';
      },
      complete: () => {
        this.loading = false;
      },
    });
  }

  applySearch(query: string): void {
    this.searchQuery = query;
    this.currentPage = 0;
    this.loadUsers(0);
  }

  previousPage(): void {
    if (this.currentPage <= 0) {
      return;
    }
    this.loadUsers(this.currentPage - 1);
  }

  nextPage(): void {
    if (this.currentPage + 1 >= this.totalPages) {
      return;
    }
    this.loadUsers(this.currentPage + 1);
  }

  startCreate(): void {
    this.editingUserId = null;
    this.errorMessage = '';
    this.successMessage = '';
    this.form.reset({
      firstname: '',
      lastname: '',
      email: '',
      password: '',
      role: 'USER',
      enabled: true,
      displayName: '',
      biography: '',
      avatarUrl: '',
    });
  }

  startEdit(user: UserProfile): void {
    this.editingUserId = user.id ?? null;
    const member = user.member;
    this.errorMessage = '';
    this.successMessage = '';
    this.form.patchValue({
      firstname: member?.firstname ?? '',
      lastname: member?.lastname ?? '',
      email: user.email ?? member?.email ?? '',
      password: '',
      role: (user.role as UserRole) ?? 'USER',
      enabled: user.enabled !== false,
      displayName: member?.displayName ?? '',
      biography: member?.biography ?? '',
      avatarUrl: member?.avatarUrl ?? '',
    });
  }

  async save(): Promise<void> {
    if (this.form.invalid || this.saving) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      const value = this.form.getRawValue();
      if (this.editingUserId == null) {
        const password = value.password?.trim() ?? '';
        if (password.length < 6) {
          this.errorMessage = 'Le mot de passe doit contenir au moins 6 caractères.';
          this.saving = false;
          return;
        }

        const payload: CreateUserPayload = {
          firstname: value.firstname?.trim() ?? '',
          lastname: value.lastname?.trim() ?? '',
          email: value.email?.trim() ?? '',
          password,
          role: value.role ?? 'USER',
        };

        await firstValueFrom(this.usersService.create(payload));
        this.successMessage = 'Utilisateur créé avec succès.';
      } else {
        const payload: UpdateUserPayload = {
          role: value.role ?? 'USER',
          enabled: !!value.enabled,
          email: value.email?.trim() || null,
          firstname: value.firstname?.trim() || null,
          lastname: value.lastname?.trim() || null,
          displayName: value.displayName?.trim() || null,
          biography: value.biography?.trim() || null,
          avatarUrl: value.avatarUrl?.trim() || null,
        };

        await firstValueFrom(this.usersService.update(this.editingUserId, payload));
        this.successMessage = 'Utilisateur modifié avec succès.';
      }

      this.startCreate();
      this.loadUsers(this.currentPage);
    } catch (error: unknown) {
      this.errorMessage = this.extractErrorMessage(error, 'Enregistrement impossible.');
    } finally {
      this.saving = false;
    }
  }

  async delete(user: UserProfile): Promise<void> {
    const userId = user.id;
    if (!userId || this.deletingUserId != null) {
      return;
    }

    if (this.auth.currentUser?.id === userId) {
      this.errorMessage = 'Tu ne peux pas supprimer ton propre compte depuis cette interface.';
      return;
    }

    const confirmed = window.confirm(`Supprimer l'utilisateur ${user.email ?? '#' + userId} ?`);
    if (!confirmed) {
      return;
    }

    this.deletingUserId = userId;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      await firstValueFrom(this.usersService.delete(userId));
      this.successMessage = 'Utilisateur supprimé.';
      this.loadUsers(this.currentPage);
    } catch (error: unknown) {
      this.errorMessage = this.extractErrorMessage(error, 'Suppression impossible.');
    } finally {
      this.deletingUserId = null;
    }
  }

  isDeleting(userId: number | undefined): boolean {
    return !!userId && this.deletingUserId === userId;
  }

  roleLabel(user: UserProfile): string {
    return user.role ? `${user.role}` : 'USER';
  }

  private extractErrorMessage(error: unknown, fallback: string): string {
    if (typeof error === 'string' && error.trim().length > 0) {
      return error;
    }

    if (error && typeof error === 'object') {
      const record = error as { error?: { message?: unknown }; message?: unknown };
      if (typeof record.error?.message === 'string' && record.error.message.trim().length > 0) {
        return record.error.message;
      }
      if (typeof record.message === 'string' && record.message.trim().length > 0) {
        return record.message;
      }
    }

    return fallback;
  }
}
